import { useEffect, useState } from 'react';
import { supabase } from '@/services/supabase';
import type { Profile, UserRole } from '@asi/shared';
import { MARKER_ICON_KEYS, MARKER_ICONS, type MarkerIconKey } from '@asi/shared';
import type { Permission } from '@asi/shared';
import {
  Users,
  UserPlus,
  Search,
  Pencil,
  ToggleLeft,
  ToggleRight,
  Trash2,
  X,
  Copy,
  CheckCircle2,
  Motorbike,
  Shield,
  User,
  Settings,
  Crown,
  Lock,
  KeyRound,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Wrench,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { usePermissions } from '@/hooks/usePermissions';
import { PermissionGate } from '@/components/PermissionGate';
import { AccountsTabs } from '@/components/AccountsTabs';
import { Pagination, usePagination } from '@/components/Pagination';

// Compact permission groups shown in the per-user additional permissions picker.
const PERM_GROUPS: { label: string; perms: Permission[] }[] = [
  { label: 'Panel y Emergencias', perms: ['dashboard.view','emergency.view','emergency.assign_patrol','emergency.update_status'] },
  { label: 'Tickets', perms: ['tickets.view','tickets.create','tickets.update','tickets.delete'] },
  { label: 'Usuarios', perms: ['users.view','users.create','users.update','users.delete','users.manage_roles'] },
  { label: 'Dispositivos', perms: ['cameras.view','cameras.manage','alarms.view','alarms.manage'] },
  { label: 'Centro ARC', perms: ['arc.events.view','arc.contacts.view','arc.contacts.manage','arc.cid_codes.view','arc.cid_codes.manage'] },
  { label: 'Cuentas', perms: ['accounts.view','accounts.manage'] },
  { label: 'Planes y Facturación', perms: ['plans.view','plans.manage','revenue.view','billing.view','billing.manage','collections.view','collections.confirm'] },
  { label: 'Comunicaciones', perms: ['communications.view','communications.send'] },
  { label: 'Sistema', perms: ['geofences.view','geofences.manage','settings.manage','audit.view','changelog.view'] },
];

const PERM_LABELS: Partial<Record<Permission, string>> = {
  'dashboard.view':'Ver dashboard','emergency.view':'Ver emergencias','emergency.assign_patrol':'Asignar patrullero','emergency.update_status':'Actualizar estado alerta',
  'tickets.view':'Ver tickets','tickets.create':'Crear tickets','tickets.update':'Editar tickets','tickets.delete':'Eliminar tickets',
  'users.view':'Ver usuarios','users.create':'Crear usuarios','users.update':'Editar usuarios','users.delete':'Eliminar usuarios','users.manage_roles':'Gestionar roles',
  'cameras.view':'Ver cámaras','cameras.manage':'Admin cámaras','alarms.view':'Ver alarmas','alarms.manage':'Admin alarmas',
  'arc.events.view':'Ver eventos ARC','arc.contacts.view':'Ver contactos ARC','arc.contacts.manage':'Admin contactos ARC','arc.cid_codes.view':'Ver códigos CID','arc.cid_codes.manage':'Admin códigos CID',
  'accounts.view':'Ver cuentas','accounts.manage':'Admin cuentas',
  'plans.view':'Ver planes','plans.manage':'Admin planes','revenue.view':'Ver ingresos','billing.view':'Ver facturación','billing.manage':'Admin facturación','collections.view':'Ver cobros','collections.confirm':'Confirmar pagos',
  'communications.view':'Ver comunicaciones','communications.send':'Enviar comunicaciones',
  'geofences.view':'Ver geocercas','geofences.manage':'Admin geocercas','settings.manage':'Configuración sistema','audit.view':'Ver auditoría','changelog.view':'Ver changelog',
};

const ROLE_CONFIG: Record<UserRole, { label: string; plural: string; color: string; bg: string; icon: React.ReactNode }> = {
  client:     { label: 'Cliente',       plural: 'Clientes',        color: 'text-blue-600 dark:text-blue-400',   bg: 'bg-blue-100 dark:bg-blue-500/20',    icon: <User className="w-4 h-4" /> },
  patrol:     { label: 'Patrullero',    plural: 'Patrulleros',     color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-500/20',  icon: <Motorbike className="w-4 h-4" /> },
  seller:     { label: 'Vendedor',      plural: 'Vendedores',      color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-500/20', icon: <Briefcase className="w-4 h-4" /> },
  technician: { label: 'Técnico',       plural: 'Técnicos',        color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-500/20', icon: <Wrench className="w-4 h-4" /> },
  operator:   { label: 'Operador',      plural: 'Operadores',      color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-500/20',  icon: <Shield className="w-4 h-4" /> },
  admin:      { label: 'Administrador', plural: 'Administradores', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-500/20', icon: <Settings className="w-4 h-4" /> },
  super_admin:{ label: 'Super Admin',   plural: 'Super Admins',    color: 'text-red-600 dark:text-red-400',     bg: 'bg-red-100 dark:bg-red-500/20',      icon: <Crown className="w-4 h-4" /> },
};

const ALL_ROLES: UserRole[] = ['client', 'patrol', 'operator', 'seller', 'technician', 'admin', 'super_admin'];

const EMPTY_FORM = { full_name: '', email: '', phone: '', role: 'client' as UserRole, marker_icon: 'person' as MarkerIconKey, shake_sos_enabled: true, shake_sos_sensitivity: 'medium' as 'low' | 'medium' | 'high', shake_sos_countdown_seconds: 10, additional_permissions: [] as Permission[] };

let _cachedUsers: Profile[] | null = null;

export function UsersPage() {
  const [users, setUsers] = useState<Profile[]>(_cachedUsers ?? []);
  const [loading, setLoading] = useState(_cachedUsers === null);
  const [roleFilter, setRoleFilter] = useState<UserRole | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<Profile | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Profile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [iconGridOpen, setIconGridOpen] = useState(false);
  const [permPickerOpen, setPermPickerOpen] = useState(false);
  const [expandedPermGroups, setExpandedPermGroups] = useState<string[]>([]);
  // After creating a user, show the generated temporary password once.
  const [createdCredentials, setCreatedCredentials] = useState<{ email: string; password: string } | null>(null);
  const [credentialsCopied, setCredentialsCopied] = useState(false);
  const { can } = usePermissions();

  async function fetchUsers() {
    if (_cachedUsers === null) setLoading(true);
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) {
      _cachedUsers = data as Profile[];
      setUsers(_cachedUsers);
    }
    setLoading(false);
  }

  useEffect(() => { fetchUsers(); }, []);

  const activeUsers  = users.filter((u) => !(u as unknown as { deleted_at?: string }).deleted_at);
  const deletedUsers = users.filter((u) =>  (u as unknown as { deleted_at?: string }).deleted_at);
  const visiblePool  = showDeleted ? deletedUsers : activeUsers;

  const filtered = visiblePool.filter((u) => {
    if (roleFilter !== 'ALL' && u.role !== roleFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        u.full_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.phone ?? '').includes(q)
      );
    }
    return true;
  });

  const pager = usePagination(filtered, `${search}|${roleFilter}|${showDeleted}`);

  const counts = ALL_ROLES.reduce<Record<string, number>>((acc, r) => {
    acc[r] = activeUsers.filter((u) => u.role === r).length;
    return acc;
  }, {});

  function openCreate() {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setError(null);
    setIconGridOpen(false);
    setShowModal(true);
  }

  function openEdit(user: Profile) {
    setEditingUser(user);
    setForm({
      full_name: user.full_name,
      email: user.email,
      phone: user.phone ?? '',
      role: user.role,
      marker_icon: ((user.marker_icon as MarkerIconKey) ?? (user.role === 'patrol' ? 'motorcycle' : 'person')),
      shake_sos_enabled: user.shake_sos_enabled !== false,
      shake_sos_sensitivity: (user.shake_sos_sensitivity ?? 'medium') as 'low' | 'medium' | 'high',
      shake_sos_countdown_seconds: user.shake_sos_countdown_seconds ?? 10,
      additional_permissions: ((user as unknown as { additional_permissions?: string[] }).additional_permissions ?? []) as Permission[],
    });
    setError(null);
    setIconGridOpen(false);
    setPermPickerOpen(false);
    setExpandedPermGroups([]);
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.full_name.trim() || !form.email.trim()) {
      setError('Nombre y correo son obligatorios.');
      return;
    }
    setSaving(true);
    setError(null);

    if (editingUser) {
      const updatePayload: Partial<Profile> & { additional_permissions?: Permission[] } = { full_name: form.full_name, phone: form.phone || null };
      if (can('users.manage_roles')) {
        updatePayload.role = form.role;
        updatePayload.additional_permissions = form.additional_permissions;
      }
      if (form.role === 'client' || form.role === 'patrol') updatePayload.marker_icon = form.marker_icon;
      updatePayload.shake_sos_enabled = form.shake_sos_enabled;
      updatePayload.shake_sos_sensitivity = form.shake_sos_sensitivity;
      updatePayload.shake_sos_countdown_seconds = form.shake_sos_countdown_seconds;
      const { error } = await supabase
        .from('profiles')
        .update(updatePayload)
        .eq('id', editingUser.id);
      if (error) { setError(error.message); setSaving(false); return; }
    } else {
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: form.email.trim(),
          full_name: form.full_name.trim(),
          phone: form.phone.trim() || null,
          role: form.role,
        },
      });
      if (error) {
        // The Supabase SDK wraps non-2xx Edge Function responses in a generic
        // FunctionsHttpError ("Edge Function returned a non-2xx status code").
        // Read the actual JSON body from error.context to show the real cause
        // (e.g. "A user with this email address has already been registered").
        let msg = error.message;
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const body = await (error as any).context?.json?.();
          if (body?.error) msg = body.error;
        } catch { /* ignore parse errors */ }
        setError(msg);
        setSaving(false);
        return;
      }
      // Surface the default password to the creator so they can communicate
      // it verbally to the new user. The user will be forced to change it on
      // first login (must_change_password=true was set on the profile).
      const defaultPassword = (data as { default_password?: string } | null)?.default_password;
      if (defaultPassword) {
        setCreatedCredentials({ email: form.email.trim(), password: defaultPassword });
        setCredentialsCopied(false);
      }
    }

    setSaving(false);
    setShowModal(false);
    fetchUsers();
  }

  async function handleResetPassword(user: Profile) {
    if (!window.confirm(`¿Enviar correo de restablecimiento de contraseña a ${user.email}?`)) return;
    const { data, error } = await supabase.functions.invoke('smtp-settings', {
      body: {
        action: 'send-reset',
        email: user.email,
        redirectTo: `${window.location.origin}/reset-password`,
      },
    });
    if (error || data?.error) {
      alert(`Error: ${error?.message ?? data?.error}`);
    } else {
      alert(`Correo enviado a ${user.email}`);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    const { data, error } = await supabase.functions.invoke('delete-user', {
      body: { userId: deleteTarget.id },
    });
    setDeleting(false);

    // When the Edge Function returns a non-2xx status, the Supabase SDK wraps
    // it in a FunctionsHttpError with a generic message. Read the actual JSON
    // body from error.context to surface the real cause to the operator.
    if (error) {
      let msg = error.message;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const body = await (error as any).context?.json?.();
        if (body?.error) msg = body.error;
      } catch { /* ignore parse errors */ }
      setDeleteTarget(null);
      alert(`Error: ${msg}`);
      return;
    }
    if (data?.error) {
      setDeleteTarget(null);
      alert(`Error: ${data.error}`);
      return;
    }

    setUsers((prev) => prev.filter((user) => user.id !== deleteTarget.id));
    _cachedUsers = null; // invalidate so next fetchUsers re-reads
    setDeleteTarget(null);
  }

  async function toggleActive(user: Profile) {
    await supabase.from('profiles').update({ is_active: !user.is_active }).eq('id', user.id);
    setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, is_active: !user.is_active } : u));
  }

  return (
    <div className="flex flex-col h-full">
      <AccountsTabs />
      {/* Header */}
      <div className="p-6 pb-4 border-b border-gray-200 dark:border-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Usuarios</h2>
            <p className="text-gray-500 dark:text-slate-400 text-sm">Clientes, patrulleros, operadores y administración</p>
          </div>
          <PermissionGate require="users.create">
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <UserPlus className="w-4 h-4" />
              Nuevo usuario
            </button>
          </PermissionGate>
        </div>

        {/* Role summary tabs */}
        <div className="flex gap-2 flex-wrap">
          {!showDeleted && (
            <>
              <button
                onClick={() => setRoleFilter('ALL')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  roleFilter === 'ALL'
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                Todos <span className="font-bold">{activeUsers.length}</span>
              </button>
              {ALL_ROLES.map((role) => {
                const cfg = ROLE_CONFIG[role];
                const isActive = roleFilter === role;
                return (
                  <button
                    key={role}
                    onClick={() => setRoleFilter(role)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? `${cfg.bg} ${cfg.color}` : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {cfg.icon}
                    {cfg.plural} <span className="font-bold">{counts[role] ?? 0}</span>
                  </button>
                );
              })}
            </>
          )}
          {/* Deleted users tab — only visible to admins who can delete */}
          {can('users.delete') && (
            <button
              onClick={() => { setShowDeleted(!showDeleted); setRoleFilter('ALL'); setSearch(''); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ml-auto ${
                showDeleted
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400'
              }`}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Eliminados {deletedUsers.length > 0 && <span className="font-bold">{deletedUsers.length}</span>}
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-6 py-3 border-b border-gray-200 dark:border-slate-800">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar por nombre, correo o teléfono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-gray-400">
            <Users className="w-10 h-10 mb-2 opacity-40" />
            <p>No se encontraron usuarios</p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800/60 text-gray-600 dark:text-slate-400 uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Nombre</th>
                  <th className="text-left px-4 py-3 font-medium">Correo / Teléfono</th>
                  <th className="text-left px-4 py-3 font-medium">Rol</th>
                  <th className="text-left px-4 py-3 font-medium">Estado</th>
                  <th className="text-left px-4 py-3 font-medium">Registro</th>
                  {showDeleted && <th className="text-left px-4 py-3 font-medium">Eliminación</th>}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
                {pager.pageItems.map((user) => {
                  const cfg = ROLE_CONFIG[user.role];
                  return (
                    <tr key={user.id} className="bg-white dark:bg-transparent hover:bg-gray-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center text-gray-600 dark:text-slate-300 font-medium text-xs">
                            {user.full_name.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900 dark:text-white">{user.full_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-slate-400">
                        <div>{user.email}</div>
                        {user.phone && <div className="text-xs text-gray-400 dark:text-slate-500">{user.phone}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
                          {cfg.icon}
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {user.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400">
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400">
                            Inactivo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-slate-500 text-xs">
                        {new Date(user.created_at).toLocaleDateString('es-GT')}
                      </td>
                      {showDeleted && (
                        <td className="px-4 py-3 text-gray-500 dark:text-slate-500 text-xs">
                          {user.deleted_at ? new Date(user.deleted_at).toLocaleDateString('es-GT') : 'Sin fecha'}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          {!showDeleted && <PermissionGate require="users.update">
                            <button
                              onClick={() => openEdit(user)}
                              className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                              title="Editar"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          </PermissionGate>}
                          {!showDeleted && <PermissionGate require="users.update">
                            <button
                              onClick={() => toggleActive(user)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                user.is_active
                                  ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-500/10'
                                  : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                              }`}
                              title={user.is_active ? 'Desactivar' : 'Activar'}
                            >
                              {user.is_active
                                ? <ToggleRight className="w-4 h-4" />
                                : <ToggleLeft className="w-4 h-4" />}
                            </button>
                          </PermissionGate>}
                          {!showDeleted && <PermissionGate require="users.update">
                            <button
                              onClick={() => handleResetPassword(user)}
                              className="p-1.5 text-amber-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-lg transition-colors"
                              title="Restablecer contraseña"
                            >
                              <KeyRound className="w-4 h-4" />
                            </button>
                          </PermissionGate>}
                          <PermissionGate require="users.delete">
                            <button
                              onClick={() => setDeleteTarget(user)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination
              className="border-t border-gray-200 px-4 py-3 dark:border-slate-800"
              page={pager.page}
              pageCount={pager.pageCount}
              pageSize={pager.pageSize}
              total={pager.total}
              onPageChange={pager.setPage}
              onPageSizeChange={pager.setPageSize}
              itemLabel="usuarios"
            />
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex flex-col items-center text-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Eliminar usuario</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                  ¿Eliminar a{' '}
                  <span className="font-semibold text-gray-800 dark:text-white">{deleteTarget.full_name}</span>?
                </p>
                <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                  Esta acción eliminará la cuenta permanentemente y no se puede deshacer.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Eliminando...</>
                ) : (
                  <><Trash2 className="w-4 h-4" />Eliminar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col">
            {/* Header - Fixed */}
            <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 dark:border-slate-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingUser ? 'Editar usuario' : 'Nuevo usuario'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-white rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Nombre completo</label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                  placeholder="Juan Pérez"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Correo electrónico</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="juan@email.com"
                  disabled={!!editingUser}
                  className={editingUser ? 'opacity-60 cursor-not-allowed' : ''}
                />
                  {editingUser && <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">El correo no se puede modificar.</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Teléfono</label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="+502 5555-5555"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Rol</label>
                {can('users.manage_roles') ? (
                  <select
                    value={form.role}
                    onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {ALL_ROLES.map((r) => (
                      <option key={r} value={r}>{ROLE_CONFIG[r].label}</option>
                    ))}
                  </select>
                ) : (
                  <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-input bg-gray-50 dark:bg-slate-800 text-sm">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_CONFIG[form.role].bg} ${ROLE_CONFIG[form.role].color}`}>
                      {ROLE_CONFIG[form.role].icon}
                      {ROLE_CONFIG[form.role].label}
                    </span>
                    <Lock className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 ml-auto" />
                  </div>
                )}
              </div>

              {(form.role === 'client' || form.role === 'patrol') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Ícono en el mapa
                  </label>
                  {/* Collapsed preview */}
                  {!iconGridOpen && (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-sm text-gray-700 dark:text-slate-300">
                        <span className="text-xl leading-none">{MARKER_ICONS[form.marker_icon]?.emoji}</span>
                        <span className="text-xs font-medium">{MARKER_ICONS[form.marker_icon]?.label}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIconGridOpen(true)}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Cambiar ícono
                      </button>
                    </div>
                  )}
                  {/* Expanded grid */}
                  {iconGridOpen && (
                    <>
                      <p className="text-xs text-gray-400 dark:text-slate-500 mb-2">
                        El color del marcador refleja el estado (en línea, en segundo plano, desconectado).
                      </p>
                      <div className="grid grid-cols-4 gap-2">
                        {MARKER_ICON_KEYS.map((key) => {
                          const sel = form.marker_icon === key;
                          const meta = MARKER_ICONS[key];
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => { setForm((f) => ({ ...f, marker_icon: key })); setIconGridOpen(false); }}
                              className={`flex flex-col items-center justify-center gap-1 py-2 rounded-lg border text-xs transition-colors ${
                                sel
                                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-2 ring-blue-500/30'
                                  : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                              }`}
                              title={meta.label}
                            >
                              <span className="text-2xl leading-none">{meta.emoji}</span>
                              <span className="text-[10px] font-medium">{meta.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div>
                <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-slate-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={form.shake_sos_enabled}
                    onChange={(e) => setForm((f) => ({ ...f, shake_sos_enabled: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-slate-100">
                      SOS por agitación y caída
                    </div>
                    <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                      La app móvil detecta sacudidas fuertes o caídas y envía un SOS automáticamente. El usuario tiene unos segundos para cancelar la falsa alarma.
                    </div>
                  </div>
                </label>

                {form.shake_sos_enabled && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1.5">
                        Sensibilidad
                      </label>
                      <div className="grid grid-cols-3 gap-1">
                        {(['low', 'medium', 'high'] as const).map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, shake_sos_sensitivity: s }))}
                            className={`py-1.5 text-xs rounded-md border transition-colors ${
                              form.shake_sos_sensitivity === s
                                ? 'border-red-500 bg-red-500/10 text-red-600 font-semibold dark:text-red-400'
                                : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                            }`}
                          >
                            {s === 'low' ? 'Baja' : s === 'medium' ? 'Media' : 'Alta'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1.5">
                        Cuenta regresiva
                      </label>
                      <div className="grid grid-cols-3 gap-1">
                        {[5, 10, 15].map((sec) => (
                          <button
                            key={sec}
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, shake_sos_countdown_seconds: sec }))}
                            className={`py-1.5 text-xs rounded-md border transition-colors ${
                              form.shake_sos_countdown_seconds === sec
                                ? 'border-red-500 bg-red-500/10 text-red-600 font-semibold dark:text-red-400'
                                : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                            }`}
                          >
                            {sec}s
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Permisos adicionales — only for staff roles when editing */}
              {editingUser && can('users.manage_roles') && !['client', 'patrol'].includes(form.role) && (
                <div className="border-t border-gray-200 dark:border-slate-700 pt-4 space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">Permisos adicionales</p>
                    <span className="text-xs text-gray-400 dark:text-slate-500">
                      Se suman a los del rol · {form.additional_permissions.length} extra
                    </span>
                  </div>
                  {PERM_GROUPS.map((g) => (
                    <div key={g.label} className="rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800/50">
                        <span className="text-xs font-semibold text-gray-600 dark:text-slate-300">{g.label}</span>
                        <button
                          type="button"
                          className="text-xs text-blue-500 hover:underline"
                          onClick={() => {
                            const all = g.perms.every((p) => form.additional_permissions.includes(p));
                            setForm((f) => ({
                              ...f,
                              additional_permissions: all
                                ? f.additional_permissions.filter((p) => !g.perms.includes(p))
                                : [...new Set([...f.additional_permissions, ...g.perms])],
                            }));
                          }}
                        >
                          {g.perms.every((p) => form.additional_permissions.includes(p)) ? 'Quitar todos' : 'Todos'}
                        </button>
                      </div>
                      <div className="divide-y divide-gray-100 dark:divide-slate-800">
                        {g.perms.map((p) => (
                          <label key={p} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/40">
                            <input
                              type="checkbox"
                              className="w-3.5 h-3.5 accent-blue-600"
                              checked={form.additional_permissions.includes(p)}
                              onChange={() => setForm((f) => ({
                                ...f,
                                additional_permissions: f.additional_permissions.includes(p)
                                  ? f.additional_permissions.filter((x) => x !== p)
                                  : [...f.additional_permissions, p],
                              }))}
                            />
                            <span className="text-xs text-gray-700 dark:text-slate-300 flex-1">{PERM_LABELS[p] ?? p}</span>
                            <span className="text-[10px] font-mono text-gray-300 dark:text-slate-600">{p}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {error && (
                <p className="text-sm text-red-500 bg-red-50 dark:bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>
              )}
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className="flex gap-3 p-6 pt-4 border-t border-gray-200 dark:border-slate-700">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Guardando...' : editingUser ? 'Guardar cambios' : 'Crear usuario'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New-user credentials modal: shows the generated temporary password ONCE. */}
      {createdCredentials && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex flex-col items-center text-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Usuario creado</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                  Comparte estas credenciales con el usuario. Al iniciar sesión por primera vez se le pedirá <strong>cambiar la contraseña</strong> obligatoriamente.
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Correo</label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-slate-800 rounded-lg text-sm font-mono text-gray-900 dark:text-white break-all">
                  {createdCredentials.email}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Contraseña por defecto</label>
                <div className="px-3 py-2 bg-gray-100 dark:bg-slate-800 rounded-lg text-sm font-mono text-gray-900 dark:text-white break-all">
                  {createdCredentials.password}
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">El usuario debe cambiarla en su primer ingreso.</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(
                      `Correo: ${createdCredentials.email}\nContraseña: ${createdCredentials.password}`,
                    );
                    setCredentialsCopied(true);
                  } catch {
                    /* clipboard unavailable */
                  }
                }}
                className="flex-1 py-2.5 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                {credentialsCopied ? (
                  <><CheckCircle2 className="w-4 h-4 text-green-500" /> Copiado</>
                ) : (
                  <><Copy className="w-4 h-4" /> Copiar credenciales</>
                )}
              </button>
              <button
                onClick={() => { setCreatedCredentials(null); setCredentialsCopied(false); }}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Listo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
